import { useEffect, useMemo, useRef, useState } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { parse } from "tldts"

import type { TrustedSourceItem } from "@/api/models"

import { Building2, Settings, Shield, User } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { TrustedSourcesDialog } from "@/features/trusted-sources/components/trusted-sources-dialog"
import { useGetTrustedSources } from "@/features/trusted-sources/hooks/use-get-trusted-sources"
import { useWebSearchEnabled } from "@/hooks/use-route-overrides"
import { cn } from "@/lib/utils"
import { type Project, ProjectService, type TrustedSourceRef } from "@/services/project-service"

type ProjectWebSearchSettingsProps = {
  project: Project | undefined
  projectId: string
  sessionType: string
  canEdit?: boolean
}

type DisplaySource = {
  name: string
  url: string
  isInProject: boolean
  isInUserList: boolean
  userSourceType?: "personal" | "company"
}

/** Extract domain name from URL, including subdomain if present. */
function extractDomainName(url: string): string {
  try {
    const parsed = parse(url)
    if (parsed.domain) {
      if (parsed.subdomain) {
        return `${parsed.subdomain}.${parsed.domain}`
      }
      return parsed.domain
    }
  } catch {
    // If parsing fails, return original URL
  }
  return url
}

export function ProjectWebSearchSettings({
  project,
  projectId,
  sessionType,
  canEdit = true,
}: ProjectWebSearchSettingsProps) {
  const queryClient = useQueryClient()
  const { t } = useTranslation("", { keyPrefix: "project_settings.web_search" })
  const { t: tTrusted } = useTranslation("", { keyPrefix: "project_settings.trusted_sources" })
  const isRouteWebSearchEnabled = useWebSearchEnabled()

  if (!isRouteWebSearchEnabled) {
    return null
  }

  const updateProject = useMutation({
    mutationFn: (data: {
      name?: string
      instructions?: string
      defaultWebSearch?: boolean
      defaultModelId?: string
      trustedSources?: TrustedSourceRef[]
      trustedSourceUrls?: string[]
    }) => ProjectService.updateProject(projectId, data),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["projects", sessionType] }),
  })

  // Get user's trusted sources
  const { data: sourcesData, isPending: isSourcesPending } = useGetTrustedSources()
  const userSources = sourcesData?.sources || []

  // Get project sources (new format) and legacy URLs
  const projectSources = useMemo(() => {
    const sources: TrustedSourceRef[] = []

    // Add sources from new format
    if (project?.trustedSources?.length) {
      sources.push(...project.trustedSources)
    }

    // Add legacy URLs (only those not already in trustedSources)
    if (project?.trustedSourceUrls?.length) {
      const existingUrls = new Set(sources.map((s) => s.url))
      project.trustedSourceUrls.forEach((url: string) => {
        if (!existingUrls.has(url)) {
          sources.push({ name: url, url }) // Temporary name, will be resolved in displaySources
        }
      })
    }

    return sources
  }, [project?.trustedSources, project?.trustedSourceUrls])

  // Create a map of user sources by URL for quick lookup (matching by URL)
  const userSourcesByUrl = useMemo(() => {
    const map = new Map<string, TrustedSourceItem>()
    userSources.forEach((source) => {
      map.set(source.url, source)
    })
    return map
  }, [userSources])

  // Remember external sources so they persist until navigation
  const [rememberedExternalSources, setRememberedExternalSources] = useState<
    Map<string, TrustedSourceRef>
  >(new Map())
  const previousProjectId = useRef(projectId)

  // Clear remembered sources when projectId changes
  useEffect(() => {
    if (previousProjectId.current !== projectId) {
      setRememberedExternalSources(new Map())
      previousProjectId.current = projectId
    }
  }, [projectId])

  // Track external sources when they appear in the project
  useEffect(() => {
    // Find external sources (not in user's list) that we haven't remembered yet
    const newSourcesToRemember = projectSources.filter(
      (source) => !userSourcesByUrl.has(source.url) && !rememberedExternalSources.has(source.url)
    )

    if (newSourcesToRemember.length > 0) {
      setRememberedExternalSources((prev) => {
        const updated = new Map(prev)
        newSourcesToRemember.forEach((source) => {
          updated.set(source.url, source)
        })
        return updated
      })
    }
  }, [projectSources, userSourcesByUrl, rememberedExternalSources])

  // Merge project sources with user sources for display
  const displaySources = useMemo(() => {
    const sourcesMap = new Map<string, DisplaySource>()

    // Process all project sources - match against user sources to get name and type
    projectSources.forEach((projectSource) => {
      const userSource = userSourcesByUrl.get(projectSource.url)
      // Use matched user source name if available, otherwise use project source name
      // For sources not in user list, extract a clean domain name from the URL
      let displayName = userSource?.name || projectSource.name || projectSource.url
      // If the name is the same as the URL (legacy URL without match), extract domain
      if (
        !userSource &&
        displayName === projectSource.url &&
        projectSource.url.startsWith("http")
      ) {
        displayName = extractDomainName(projectSource.url)
      }
      sourcesMap.set(projectSource.url, {
        name: displayName,
        url: projectSource.url,
        isInProject: true,
        isInUserList: !!userSource,
        userSourceType: userSource?.type,
      })
    })

    // Add user sources that aren't in project (so they can be added)
    userSources.forEach((userSource) => {
      if (!sourcesMap.has(userSource.url)) {
        sourcesMap.set(userSource.url, {
          name: userSource.name,
          url: userSource.url,
          isInProject: false,
          isInUserList: true,
          userSourceType: userSource.type,
        })
      }
    })

    // Add remembered external sources not in project
    rememberedExternalSources.forEach((externalSource) => {
      if (!sourcesMap.has(externalSource.url)) {
        let displayName = externalSource.name || externalSource.url
        if (displayName === externalSource.url && externalSource.url.startsWith("http")) {
          displayName = extractDomainName(externalSource.url)
        }
        sourcesMap.set(externalSource.url, {
          name: displayName,
          url: externalSource.url,
          isInProject: false,
          isInUserList: false,
          userSourceType: undefined,
        })
      }
    })

    return Array.from(sourcesMap.values()).sort((a, b) => {
      // Sort: external sources first, then company, then personal, then alphabetically

      // Helper to get category priority (lower = higher priority)
      const getCategoryPriority = (source: DisplaySource) => {
        // "Not in your list" sources (from collaborators, enabled or remembered)
        if (!source.isInUserList) return 0
        // Company sources
        if (source.userSourceType === "company") return 1
        // Personal sources
        if (source.userSourceType === "personal") return 2
        // Sources without a type (shouldn't happen, but fallback)
        return 3
      }

      const priorityA = getCategoryPriority(a)
      const priorityB = getCategoryPriority(b)

      if (priorityA !== priorityB) {
        return priorityA - priorityB
      }

      // Within same category, sort alphabetically by name
      return a.name.localeCompare(b.name)
    })
  }, [projectSources, userSourcesByUrl, userSources, rememberedExternalSources])

  const [removalConfirmDialog, setRemovalConfirmDialog] = useState<{
    open: boolean
    source: DisplaySource | null
  }>({ open: false, source: null })

  const toggleTrustedSource = (source: DisplaySource, checked: boolean) => {
    if (!canEdit) return

    // If unchecking a source not in user's list, show confirmation
    if (!checked && source.isInProject && !source.isInUserList) {
      setRemovalConfirmDialog({ open: true, source })
      return
    }

    performToggle(source, checked)
  }

  const performToggle = (source: DisplaySource, checked: boolean) => {
    // Get current state
    const currentTrustedSources = project?.trustedSources || []
    const currentLegacyUrls = project?.trustedSourceUrls ?? []

    // Remove from both formats
    const updatedTrustedSources = currentTrustedSources.filter((s) => s.url !== source.url)
    const updatedLegacyUrls = currentLegacyUrls.filter((url: string) => url !== source.url)

    if (checked) {
      const userSource = userSourcesByUrl.get(source.url)
      const sourceName = userSource?.name || source.name

      // Use new format with name - this preserves names from shared projects
      updatedTrustedSources.push({
        name: sourceName,
        url: source.url,
      })
    }

    // Send update - always send both arrays (empty arrays clear the field)
    updateProject.mutate({
      trustedSources: updatedTrustedSources,
      trustedSourceUrls: updatedLegacyUrls,
    })
    setRemovalConfirmDialog({ open: false, source: null })
  }

  const [trustedSourcesDialogOpen, setTrustedSourcesDialogOpen] = useState(false)

  const isWebSearchEnabled = !!project?.defaultWebSearch

  return (
    <>
      <div className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-md border p-3">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium" htmlFor="default-web-search">
                {t("enable_web_search")}
              </Label>
              <p className="text-muted-foreground text-xs">{t("enable_web_search_description")}</p>
            </div>
            <Switch
              checked={isWebSearchEnabled}
              disabled={!canEdit}
              id="default-web-search"
              onCheckedChange={(v) => canEdit && updateProject.mutate({ defaultWebSearch: !!v })}
            />
          </div>
        </div>

        {isWebSearchEnabled && (
          <>
            <Separator />

            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium">{t("trusted_sources_title")}</h4>
                <p className="text-muted-foreground mt-1 text-xs">
                  {t("trusted_sources_description")}
                </p>
              </div>

              {isSourcesPending && (
                <div className="flex items-center gap-2 py-4">
                  <Spinner className="h-4 w-4" />
                  <span className="text-muted-foreground text-sm">{t("loading_sources")}</span>
                </div>
              )}

              {!isSourcesPending && displaySources.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Shield className="text-muted-foreground/50 mb-2 h-8 w-8" />
                  <p className="text-muted-foreground text-sm">{t("no_sources")}</p>
                </div>
              )}

              {!isSourcesPending && displaySources.length > 0 && (
                <div className="space-y-2">
                  {displaySources.map((source) => {
                    const sourceId = source.url

                    return (
                      <div
                        key={sourceId}
                        className={cn(
                          "bg-card flex items-center justify-between rounded-lg border p-3 transition-colors",
                          canEdit && "hover:bg-accent/50"
                        )}
                      >
                        <div className="grid min-w-0 flex-1 gap-1.5 leading-none">
                          <div className="flex items-center gap-2">
                            <Label className="text-sm font-medium" htmlFor={`source-${sourceId}`}>
                              {source.name}
                            </Label>
                            {source.userSourceType === "company" && (
                              <Badge
                                className="h-5 gap-1 text-xs dark:text-white"
                                variant="secondary"
                              >
                                <Building2 className="h-3 w-3" />
                                {tTrusted("company_source", { defaultValue: "Company" })}
                              </Badge>
                            )}
                            {source.userSourceType === "personal" && (
                              <Badge className="h-5 gap-1 text-xs" variant="outline">
                                <User className="h-3 w-3" />
                                {tTrusted("personal_source", { defaultValue: "Personal" })}
                              </Badge>
                            )}
                            {!source.isInUserList && source.isInProject && (
                              <Badge className="h-5 gap-1 text-xs" variant="outline">
                                <span className="bg-warning h-1.5 w-1.5 rounded-full" />
                                {tTrusted("not_in_your_list", { defaultValue: "Not in your list" })}
                              </Badge>
                            )}
                          </div>
                          <p className="text-muted-foreground text-xs break-all">{source.url}</p>
                        </div>
                        <Switch
                          checked={source.isInProject}
                          disabled={!canEdit}
                          id={`source-${sourceId}`}
                          onCheckedChange={(v) => toggleTrustedSource(source, !!v)}
                        />
                      </div>
                    )
                  })}
                </div>
              )}

              {canEdit && (
                <Button
                  className="w-full gap-2"
                  variant="outline"
                  onClick={() => setTrustedSourcesDialogOpen(true)}
                >
                  <Settings className="h-4 w-4" />
                  {t("manage_sources")}
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      <TrustedSourcesDialog
        open={trustedSourcesDialogOpen}
        onOpenChange={setTrustedSourcesDialogOpen}
      />

      {/* Confirmation dialog for removing sources not in user's list */}
      <AlertDialog
        open={removalConfirmDialog.open}
        onOpenChange={(open) => setRemovalConfirmDialog({ open, source: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {tTrusted("remove_source_title", { defaultValue: "Remove Trusted Source?" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {tTrusted("remove_source_warning", {
                defaultValue:
                  "Removing this source might make it hard to re-add later since it's not in your personal or company list. Are you sure you want to remove it?",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {tTrusted("common.cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (removalConfirmDialog.source) {
                  performToggle(removalConfirmDialog.source, false)
                }
              }}
            >
              {tTrusted("remove", { defaultValue: "Remove" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
